/**
 * The library's filter panel — the 07.09 programator mock's own, drawn at 320px.
 *
 * It was `features/exercise-library-rail/ui/RailFilterTabs` until 17 September 2026 and moved down
 * here unchanged, when the survey editor's recording picker became the same library in a dialog
 * (owner: „wyciągnij ją do wspólnego komponentu, filtry i szukajka 1:1"). It never knew anything
 * about a rail: it renders `exerciseFacetGroups` and dispatches `toggleExerciseFacet`, and the
 * signed-in trainer arrives as a PROP — so `entities/session` is still not reachable from here.
 *
 * A fifth tab, `Język`, follows them since 25 September 2026 (owner: its own tab, not a footer) —
 * on a host that opts in through `withLanguageTab`, which none keyed by the content language does.
 * It is the content language, not a facet — `ExerciseLanguageChoice` says why — so it has no option
 * search, no count and nothing for `Wyczyść` to clear.
 *
 * Four facet tabs (`Sprzęt`, `Typ`, `Mięśnie`, `Źródło`), one facet behind each, and **three different
 * renderings inside them**, which is the mock's decision and not an unfinished one:
 *
 *   · `Typ`     pills — h30, fully rounded, no colour: a category is grey here (owner, 9 Sep);
 *   · `Sprzęt`  tiles — `repeat(3, 1fr)`, a 40x40 square carrying the piece's initial;
 *   · `Mięśnie` / `Źródło`  rows — full width, a tick at the right end when ticked.
 *
 * **The `Mięśnie` rows carry NO colour dot**, and that is the mock rather than an omission: its
 * option data gives a colour to the six TYPES and to nothing else, so its muscle rows are a word
 * apiece. The 02.08 gallery's own wall put a `--mg-*` dot on its muscle chips because that canon
 * draws one there; two canons, two drawings, and this surface follows its own.
 *
 * Unifying them would be a shortcut past the drawing, not a simplification: a wall of 15 equipment
 * names does not read as pills and 6 types do not need 15 rows of vertical space. The three shapes
 * differ in the ONE dimension that matters at 320px, which is how much room an option costs.
 *
 * ── Everything shipped is multi-select except `Źródło` ───────────────────────────────────────
 * The mock's own rule, and the entity already holds it: origin is single-select and `Wszystkie` is
 * its reset. The dispatch differs accordingly — a ticked origin pressed again is `remove`, which
 * returns to `all` rather than leaving the query with no origin at all.
 *
 * ── The tab's own search field is the mock's, and it WORKS ───────────────────────────────────
 * The mock draws "Szukaj sprzętu" / "Szukaj mięśni" over those two tabs and nothing behind them.
 * Fifteen pieces of equipment and fourteen groups inside a 250px scroller is exactly the list a
 * trainer reaches for that field on, so it filters the OPTIONS — never the results, which is the
 * outer field's job. It resets when the tab changes: a term typed against equipment means nothing
 * against muscles, and carrying it over would show an empty tab with no visible cause.
 *
 * ── Two marks the owner added over the mock, on `Źródło` ─────────────────────────────────────
 * Owner instruction, 9 September 2026: the two real origins get a mark each — MoveBuddy takes the
 * brand mark, "Moje" takes the trainer's own avatar with their initials behind it. Both are the
 * app's existing assets (`shared/ui/brandMark.module.css`, `shared/ui/AvatarChip`) rather than
 * anything drawn here; the app never draws its own copy of a logo. **Neither mark carries the
 * meaning**: the row still says the word, because a filter identified only by a picture cannot be
 * read by a screen reader or by anyone who has not learnt the icon. `Wszystkie` takes no mark — it
 * is the absence of a source rather than a third one.
 */

import { useState } from 'react';

import type { ExerciseFacetGroupView, ExerciseFacetKey } from '../model/exerciseFacets';
import { foldPolish } from '~/shared/lib/foldPolish';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import brand from '~/shared/ui/brandMark.module.css';
import photoBox from '~/shared/ui/photoBox.module.css';

import { ExerciseLanguageChoice } from './ExerciseLanguageChoice';
import styles from './ExerciseLibraryFilterTabs.module.css';

/** Who is signed in, for the `Moje` row's mark. Both fields come off the session. */
export interface ExerciseLibraryTrainer {
  readonly initials: string;
  readonly avatarUrl: string | null;
}

export interface ExerciseLibraryFilterTabsProps {
  readonly id: string;
  /** The four groups, in `EXERCISE_FACET_KEYS` order. Rendered by facet, not by position. */
  readonly groups: readonly ExerciseFacetGroupView[];
  readonly onToggle: (facet: ExerciseFacetKey, id: string) => void;
  /** Pressing an already-ticked option. Distinct from `onToggle` only for single-select origin. */
  readonly onRemove: (facet: ExerciseFacetKey, id: string) => void;
  readonly onClear: () => void;
  /** `Pokaż N` — the live match count. Pressing it puts the panel away. */
  readonly resultCount: number;
  readonly onApply: () => void;
  readonly trainer: ExerciseLibraryTrainer;
  readonly labels: ExerciseLibraryFilterLabels;
  /**
   * Draw the fifth tab, `Język`. OFF by default: switching the content language re-keys every
   * exercise read, and a host whose screen is keyed by it (the builder's plan, the calendar dock's
   * day, the Forma list) would reset under the trainer — unsaved edits and undo with it.
   */
  readonly withLanguageTab?: boolean;
}

/**
 * The panel's five tabs: the four facets, and `language` — the content language, which is not a
 * facet (see `ExerciseLanguageChoice`) and therefore never carries a count.
 */
export type ExerciseLibraryFilterTab = ExerciseFacetKey | 'language';

/** Everything the panel says. Assembled by the rail, because this is `ui/` and copy is the host's. */
export interface ExerciseLibraryFilterLabels {
  /** The tab's own word. A ticked count is appended by this component, as the mock appends it. */
  readonly tab: (tab: ExerciseLibraryFilterTab) => string;
  readonly tabWithCount: (label: string, count: number) => string;
  /** The panel's accessible name, and the tablist's. */
  readonly panel: string;
  readonly optionName: (label: string, count: number) => string;
  readonly optionSearch: (facet: ExerciseFacetKey) => string;
  readonly originHint: (id: string) => string;
  readonly empty: string;
  readonly clear: string;
  readonly apply: string;
}

/** The mock's own tab order, which is not the facet model's. */
const FACET_TABS: readonly ExerciseLibraryFilterTab[] = ['equipment', 'types', 'muscles', 'origin'];

/** The same, and the owner's `Język` after it, for a host that offers it. */
const WITH_LANGUAGE: readonly ExerciseLibraryFilterTab[] = [...FACET_TABS, 'language'];

/** The mock opens on `Typ`. */
const INITIAL_TAB: ExerciseLibraryFilterTab = 'types';

/**
 * The `Sprzęt` tile's face — the piece's own photograph, and its initial when there is none.
 *
 * The mock draws a letter because the prototype had no pictures. The backend ships one for every
 * piece the catalogue names, out of `SeedAssets/equipment` beside the exercise covers, so the tile
 * draws the thing rather than the first letter of its word.
 *
 * It is a component rather than a branch inside the map because of the ONE `useState`: a cover the
 * catalogue names and the media root does not hold must fall back to the letter, and a failed `<img>`
 * with `alt=""` is not nothing on screen — Chrome draws its broken-file mark, which is worse than
 * the letter this replaces. That is the same fallback, for the same reason, that `ExerciseLibraryRow` already
 * applies to a movement's still. Keyed by the piece's id, so the flag cannot carry to another tile.
 */
function EquipmentTile({ imageUrl, label }: { imageUrl: string | null; label: string }) {
  const [failed, setFailed] = useState(false);

  if (imageUrl === null || failed) {
    return (
      <i className={styles.tile} aria-hidden="true">
        {label.slice(0, 1).toUpperCase()}
      </i>
    );
  }

  return (
    <img
      className={`${String(styles.tile)} ${String(styles.tilePhoto)} ${String(photoBox.photo)}`}
      src={imageUrl}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => {
        setFailed(true);
      }}
    />
  );
}

export function ExerciseLibraryFilterTabs({
  id,
  groups,
  onToggle,
  onRemove,
  onClear,
  resultCount,
  onApply,
  trainer,
  labels,
  withLanguageTab = false,
}: ExerciseLibraryFilterTabsProps) {
  const tabs = withLanguageTab ? WITH_LANGUAGE : FACET_TABS;
  // ONE `useState` for the tab and its search term, because they move together: switching tabs
  // clears the term. Two would be two setters that each have to remember the other, which is how a
  // term typed against equipment survives onto the muscle tab and empties it for no visible reason.
  const [view, setView] = useState<{ tab: ExerciseLibraryFilterTab; term: string }>({
    tab: INITIAL_TAB,
    term: '',
  });

  // The facet behind the open tab, or `null` on `Język`, which is not one.
  const facet = view.tab === 'language' ? null : view.tab;
  const group = groups.find((candidate) => candidate.facet === facet) ?? null;
  const isTiles = view.tab === 'equipment';
  const isPills = view.tab === 'types';
  // The mock draws the option search over the two long lists only. `Typ` is six and `Źródło` three.
  const optionSearchFacet =
    view.tab === 'equipment' || view.tab === 'muscles' ? view.tab : null;
  const hasOptionSearch = optionSearchFacet !== null;

  // Whether a source is actually picked. `Wszystkie` is never one of them — see the option below.
  const hasOrigin = (groups.find((candidate) => candidate.facet === 'origin')?.chips ?? []).some(
    (chip) => chip.isSelected,
  );

  const terms = foldPolish(view.term);
  const chips = (group?.chips ?? []).filter(
    (chip) => !hasOptionSearch || terms.length === 0 || foldPolish(chip.label).includes(terms),
  );

  return (
    <div className={styles.panel} id={id}>
      {/* Real tab semantics, which means the arrow keys and ONE tab stop — a `role="tablist"` whose
        * four buttons are four stops is a tablist that lies to the thing reading it. */}
      <div
        className={styles.tabs}
        role="tablist"
        aria-label={labels.panel}
        onKeyDown={(event) => {
          const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (step === 0) return;
          event.preventDefault();
          const at = tabs.indexOf(view.tab);
          const next = tabs[(at + step + tabs.length) % tabs.length];
          if (next === undefined) return;
          setView({ tab: next, term: '' });
          // Focus follows selection, which is the APG's automatic-activation pattern and the right
          // one here: switching tabs costs nothing, so nobody should have to press Enter as well.
          globalThis.document.getElementById(`${id}-tab-${next}`)?.focus();
        }}
      >
        {tabs.map((tab) => {
          // `Język` has no group, so it never carries a count — it is not a filter.
          const owner = groups.find((candidate) => candidate.facet === tab) ?? null;
          const selected = (owner?.chips ?? []).filter((chip) => chip.isSelected).length;
          const word = labels.tab(tab);
          const isOpen = view.tab === tab;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`${id}-tab-${tab}`}
              aria-selected={isOpen}
              aria-controls={`${id}-options`}
              tabIndex={isOpen ? 0 : -1}
              className={`${String(styles.tab)} ${isOpen ? String(styles.tabOn) : ''}`}
              onClick={() => {
                setView({ tab, term: '' });
              }}
            >
              {selected === 0 ? word : labels.tabWithCount(word, selected)}
            </button>
          );
        })}
      </div>

      {optionSearchFacet !== null && (
        <label className={styles.optionSearch}>
          <svg className={styles.optionSearchIcon} viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" />
            <path d="M16 16l4 4" />
          </svg>
          <input
            className={styles.optionSearchInput}
            type="text"
            autoComplete="off"
            aria-label={labels.optionSearch(optionSearchFacet)}
            placeholder={labels.optionSearch(optionSearchFacet)}
            value={view.term}
            onChange={(event) => {
              setView((current) => ({ ...current, term: event.target.value }));
            }}
            onKeyDown={(event) => {
              // The drawer's own Escape closes the library; inside this field it belongs to the
              // field. Stopped here so a trainer clearing a term does not lose the whole catalogue.
              if (event.key === 'Escape') event.stopPropagation();
            }}
          />
        </label>
      )}

      <div
        className={`${String(styles.options)} ${isTiles ? String(styles.optionsTiles) : ''} ${
          isPills ? String(styles.optionsPills) : ''
        }`}
        id={`${id}-options`}
        role="tabpanel"
        aria-labelledby={`${id}-tab-${view.tab}`}
      >
        {facet === null ? (
          <ExerciseLanguageChoice titleClassName={String(styles.languageTitle)} />
        ) : chips.length === 0 ? (
          <p className={styles.empty}>{labels.empty}</p>
        ) : (
          chips.map((chip) => {
            // `Wszystkie` reads as ticked when no source is picked, which is the mock's own rule.
            // The facet model deliberately never marks it selected — in the 460px column that would
            // make a group's summary read "Wszystkie" twice, once as the ticked label and once as
            // the word for "nothing is ticked" — and there is no summary here, so the panel says it
            // itself rather than the entity changing under the surface that needs the other answer.
            const isOn =
              chip.isSelected || (view.tab === 'origin' && chip.id === 'all' && !hasOrigin);
            // No colour on a category, ever, in this view (owner, 9 September): the muscle chips
            // beside these already own a colour scale, and two categorical palettes on one
            // surface made a type read as a body part. A category is a grey pill here.
            const hint = view.tab === 'origin' ? labels.originHint(chip.id) : '';
            return (
              <button
                key={chip.id}
                type="button"
                className={`${String(styles.option)} ${isOn ? String(styles.optionOn) : ''}`}
                aria-pressed={isOn}
                // The COUNT rides in the accessible name rather than on the option's face, exactly
                // as it does in `RailFilters` and for the mock's own reason: the mock draws no
                // numbers here, and a 320px tile that printed one would not hold its word.
                aria-label={labels.optionName(chip.label, chip.count)}
                title={labels.optionName(chip.label, chip.count)}
                onClick={() => {
                  if (isOn) onRemove(facet, chip.id);
                  else onToggle(facet, chip.id);
                }}
              >
                {isTiles && (
                  <EquipmentTile key={chip.id} imageUrl={chip.imageUrl} label={chip.label} />
                )}
                {/* The owner's two marks. Decorative in both cases — the word beside each says the
                  * same thing, and a mark announced as well would be noise. */}
                {view.tab === 'origin' && chip.id === 'library' && (
                  <span className={`${brand.brandMark} ${String(styles.brand)}`} aria-hidden="true" />
                )}
                {view.tab === 'origin' && chip.id === 'mine' && (
                  <AvatarChip
                    initials={trainer.initials}
                    avatarUrl={trainer.avatarUrl}
                    variant="library"
                    className={styles.avatar}
                  />
                )}
                <span className={styles.optionBody}>
                  <b className={styles.optionName}>{chip.label}</b>
                  {hint.length > 0 && <b className={styles.optionHint}>{hint}</b>}
                </span>
                {/* A tick on a ROW, never on a tile. A tile carries a photograph of the
                  * equipment and its own word; the owner asked for the selection to be the ring
                  * alone (9 September), and a tick under the label grew the grid row on every
                  * press. `aria-pressed` carries the state to everything that cannot see a ring. */}
                {!isPills && !isTiles && isOn && (
                  <i className={styles.tick} aria-hidden="true">
                    ✓
                  </i>
                )}
              </button>
            );
          })
        )}
      </div>

      <div className={styles.foot}>
        <button type="button" className={styles.clear} onClick={onClear}>
          {labels.clear}
        </button>
        <span className={styles.footSpacer} />
        <button type="button" className={styles.apply} onClick={onApply}>
          {labels.apply}
          <i className={styles.applyCount}>{resultCount}</i>
        </button>
      </div>
    </div>
  );
}
